import { InventoryV2, ModelStatus, ProductType } from '~/supabase/types'
import { FetchedInventory } from '../hooks/useGetInventory'
import * as R from 'remeda'
import { ringSizes } from '~/utils/constants'
import { ShopifyCreateVariantsParams } from '../../../shared/schemas/shopify'
import { CreateVariantsResponse } from '../../../shared/schemas/shopify'

const appEnv = import.meta.env.VITE_APP_ENV

if (appEnv === 'development') {
  console.log('shopify running in dev mode')
}

export const SHOPIFY_LOCATION_ID =
  appEnv === 'production'
    ? 'gid://shopify/Location/72811675761'
    : 'gid://shopify/Location/109210403163'

export const shopifyCategoryMap: Record<ProductType, string> = {
  earring: 'gid://shopify/TaxonomyCategory/aa-6-6',
  pendant: 'gid://shopify/TaxonomyCategory/hg-13-13-1-3',
  gemstone: 'gid://shopify/TaxonomyCategory/ae-2-2-6-2',
  necklace: 'gid://shopify/TaxonomyCategory/aa-6-8',
  ring: 'gid://shopify/TaxonomyCategory/aa-6-9',
  bracelet: 'gid://shopify/TaxonomyCategory/aa-6-3',
  chain: 'gid://shopify/TaxonomyCategory/aa-6-2',
}

export const getVariantsFromCreatedProducts = (
  products: {
    shopify_product_id: string
    stock_code: string
    product_type: ProductType
    id: number
    status_v2: ModelStatus
    price: number
  }[],
  productInventoryMap: Record<string, Record<number, FetchedInventory[]>>
): ShopifyCreateVariantsParams[] =>
  products.flatMap((product) => {
    const inventorySizeMap = productInventoryMap[product.stock_code]

    if (!inventorySizeMap) return []

    const price = product.price

    const inventoryNotAvailable = R.values(inventorySizeMap).flat().length === 0
    if (inventoryNotAvailable)
      return [
        {
          productId: product.shopify_product_id,
          variants:
            product.product_type === 'ring'
              ? R.values(createDefaultRingVariants(price))
              : ([
                  {
                    compareAtPrice: price,
                    optionValues: [{ optionName: 'size', name: '0' }],
                    price,
                    inventoryQuantities: {
                      availableQuantity: 0,
                      locationId: SHOPIFY_LOCATION_ID,
                    },
                  },
                ] as const),
          strategy: 'REMOVE_STANDALONE_VARIANT',
        },
      ]

    const productVariants = R.entries(inventorySizeMap).flatMap(([size, inventoryList]) => {
      return {
        optionValues: [{ optionName: 'size', name: size } as const],
        price,
        compareAtPrice: price,
        inventoryQuantities: {
          availableQuantity: inventoryList.length,
          locationId: SHOPIFY_LOCATION_ID,
        } as const,
      }
    })
    const productVariantsMap = R.mapToObj(productVariants, (item) => [
      item.optionValues[0]?.name ?? '-',
      item,
    ])

    const defaultVariantsMap =
      product.product_type === 'ring' ? createDefaultRingVariants(price) : {}

    return {
      productId: product.shopify_product_id,
      variants: R.values({ ...defaultVariantsMap, ...productVariantsMap }),
      strategy: 'REMOVE_STANDALONE_VARIANT',
    } as const
  })

export const createDefaultRingVariants = (
  price: number
): Record<string, ShopifyCreateVariantsParams['variants'][number]> => {
  return R.mapToObj(
    ringSizes.map((size) => ({
      optionValues: [{ optionName: 'size', name: size } as const],
      price,
      compareAtPrice: price,
      inventoryQuantities: {
        availableQuantity: 0,
        locationId: SHOPIFY_LOCATION_ID,
      } as const,
    })),
    (item) => [item.optionValues[0]?.name ?? '-', item]
  )
}

export const getInventoryToUpdate = (
  variantsResponse: CreateVariantsResponse[],
  productInventoryMap: Record<string, Record<number, [FetchedInventory, ...FetchedInventory[]]>>
) =>
  variantsResponse.flatMap((variant) => {
    const inventorySizeMap = productInventoryMap[variant.product.title]

    if (!inventorySizeMap) return []

    return variant.createdVariants.flatMap((variant) => {
      const size = variant.selectedOptions.find((option) => option.name === 'size')?.value
      const numberSize = size ? parseInt(size) : null

      if (Number.isNaN(numberSize) || numberSize === null) return []
      const inventory = inventorySizeMap[numberSize]

      if (!inventory) return []

      return inventory.map(
        (inv) =>
          ({
            id: inv.id,
            inventory_item_id: variant.inventoryItem.id,
            product_variant_id: variant.id,
          }) as const satisfies Partial<InventoryV2>
      )
    })
  })

const storeDomain = appEnv === 'development' ? 'wollem-develop' : 'wollem-prod'

export const createShopifyVariantUrl = (shopifyProductId: string, variantId: string) =>
  `https://admin.shopify.com/store/${storeDomain}/products/${shopifyProductId.split('/').pop()}/variants/${variantId.split('/').pop()}`

export const createShopifyProductUrl = (shopifyProductId: string) =>
  `https://admin.shopify.com/store/${storeDomain}/products/${shopifyProductId.split('/').pop()}`

export const createShopifyOrderUrl = (shopifyOrderId: string) =>
  `https://admin.shopify.com/store/${storeDomain}/orders/${shopifyOrderId.split('/').pop()}`
